import { Action, Reducer } from 'redux'
import { createSelector, OutputSelector } from 'reselect'
import {
  IShipperLane,
  IShipperLanesParams,
  IShipperLanesState,
  SHIPPER_LANE_STATUS,
} from 'apps/marketplace/store/shipperLanes/types'
import { IMarketplaceStore } from '../types'
import { FETCH_SHIPPER_LANES_ACTIONS, FETCH_SHIPPER_LANE_COUNTS_ACTIONS as c } from './actions'

const {
  FETCH_SHIPPER_LANES_FAILURE,
  FETCH_SHIPPER_LANES_REQUEST,
  FETCH_SHIPPER_LANES_SUCCESS,
} = FETCH_SHIPPER_LANES_ACTIONS

interface IRequestAction extends Action<string> {
  payload: {
    requestData: {}
  }
}

interface ISuccessAction extends Action<string> {
  payload: {
    shipperLanes: IShipperLane[]
    totalCount: number
  }
}

interface IErrorAction extends Action<string> {
  error: string
}

interface ICountSuccessAction extends Action<string> {
  payload: {
    count: number[]
  }
}

type ShipperLanesAction = IRequestAction | ISuccessAction | IErrorAction | ICountSuccessAction

const defaultState: IShipperLanesState = {
  fetching: false,
  shipperLanes: [],
  shipperLaneCounts: [],
  totalCount: 0,
  params: {
    pageCount: 0,
    perPage: 15,
    filters: {
      activeStatus: SHIPPER_LANE_STATUS.ACTIVE,
    },
  },
}

/** Reducer */
const shipperLanesReducer: Reducer<IShipperLanesState, ShipperLanesAction> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case FETCH_SHIPPER_LANES_REQUEST: {
      return {
        ...state,
        fetching: true,
        error: undefined,
      }
    }
    case FETCH_SHIPPER_LANES_SUCCESS: {
      const {
        payload: { shipperLanes, totalCount },
      } = action as ISuccessAction
      return {
        ...state,
        fetching: false,
        shipperLanes,
        totalCount,
      }
    }
    case FETCH_SHIPPER_LANES_FAILURE: {
      return {
        ...state,
        fetching: false,
        error: (action as IErrorAction).error,
      }
    }
    case c.FETCH_SHIPPER_LANE_COUNTS_SUCCESS: {
      const {
        payload: { count },
      } = action as ICountSuccessAction
      return {
        ...state,
        shipperLaneCounts: count,
      }
    }
    default:
      return state
  }
}

export default shipperLanesReducer

/** Selectors */
const selectShipperLanes = (state: IMarketplaceStore): IShipperLanesState => state.shipperLanes
const selectShipperLanesList = (state: IMarketplaceStore): IShipperLane[] =>
  state.shipperLanes.shipperLanes
const selectShipperLaneCounts = (state: IMarketplaceStore): number[] =>
  state.shipperLanes.shipperLaneCounts

interface SelectShipperLanesState
  extends OutputSelector<
    IMarketplaceStore,
    {
      fetching: boolean
      error?: string
      params: IShipperLanesParams
      totalCount: number
    },
    (
      res: IShipperLanesState
    ) => {
      fetching: boolean
      error?: string
      params: IShipperLanesParams
      totalCount: number
    }
  > {}

interface SelectShipperLanesList
  extends OutputSelector<
    IMarketplaceStore,
    { shipperLanes: IShipperLane[] },
    (res: IShipperLane[]) => { shipperLanes: IShipperLane[] }
  > {}

interface SelectShipperLaneCounts
  extends OutputSelector<
    IMarketplaceStore,
    { shipperLaneCounts: number[] },
    (res: number[]) => { shipperLaneCounts: number[] }
  > {}

export const shipperLanesStateSelector: SelectShipperLanesState = createSelector(
  selectShipperLanes,
  ({ error, fetching, params, totalCount }) => ({ fetching, error, params, totalCount })
)

export const shipperLanesListSelector: SelectShipperLanesList = createSelector(
  selectShipperLanesList,
  shipperLanes => ({ shipperLanes })
)

export const shipperLaneCountsSelector: SelectShipperLaneCounts = createSelector(
  selectShipperLaneCounts,
  shipperLaneCounts => ({ shipperLaneCounts })
)
